/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: FavoriteContext.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  28.10.22 8:55 am.
 * Last modified: 28.10.22 8:55 am.
 *
 * More information at: https://egt-digital.com/
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { FAVORITE_SECTION_NAME } from '../constants';
import ISectionSettings from '../interfaces/ISectionSettings';

const defaultFavorites = {
    name: FAVORITE_SECTION_NAME,
    jackpotName: null,
    icon: 'favorites_icon',
    banner: false,
    games: [],
    gridBlocks: 0,
};

interface IFavoritesContextType {
    favorites: ISectionSettings;
    setFavorites?: React.Dispatch<React.SetStateAction<ISectionSettings>>;
    subject?: BehaviorSubject<ISectionSettings>;
}

const FavoritesContext = createContext<IFavoritesContextType>({
    favorites: defaultFavorites,
});

export const FavoritesProvider = ({
    value: subject,
    children,
}: {
    value: BehaviorSubject<ISectionSettings>;
    children: any;
}) => {
    const [favorites, setFavorites] = useState<ISectionSettings>(subject.value || defaultFavorites);

    useEffect(() => {
        const subscription = subject.subscribe(value => {
            if (value) {
                setFavorites(value);
            }
        });

        return () => subscription.unsubscribe();
    }, [subject]);

    return (
        <FavoritesContext.Provider value={{ favorites, setFavorites, subject }}>{children}</FavoritesContext.Provider>
    );
};

export const useFavorites = (): [
    ISectionSettings,
    React.Dispatch<React.SetStateAction<ISectionSettings>> | ((favorites: ISectionSettings) => void) | undefined,
] => {
    const { favorites, setFavorites: setFavoritesReactDispatcher, subject } = useContext(FavoritesContext);
    const setFavorites = subject
        ? (newFavorites: ISectionSettings) => subject.next(newFavorites)
        : setFavoritesReactDispatcher;

    return [favorites, setFavorites];
};

export const useFavoritesGameKeysArray = (): string[] => {
    const { favorites } = useContext(FavoritesContext);

    return favorites.games.map(game => game.gameKey);
};

export const useIsFavoriteGame = (gameKey: string): boolean => {
    const favoritesGameKeys = useFavoritesGameKeysArray();

    return favoritesGameKeys.includes(gameKey);
};
